/**
 * Saved Cart Service
 * Handles cart persistence to Firestore for session recovery
 *
 * Saves cart lines to Firestore so customers can resume their order
 * after leaving/closing the site. Prices are NOT saved - they are
 * recalculated on restore using current catalog prices.
 */
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore'
import { getFirebaseDb } from '@core/firebase/firebase'

const COLLECTION = 'SavedCarts'

/**
 * Save cart to Firestore
 * @param {string} userId - User UID
 * @param {Array} lines - Array of cart lines [{makat, qty, addedAt, updatedAt}]
 * @returns {Promise<void>}
 */
export async function saveCart(userId, lines) {
  if (!userId) return

  const db = getFirebaseDb()

  // If cart is empty, delete the document
  if (!lines || lines.length === 0) {
    await deleteDoc(doc(db, COLLECTION, userId))
    return
  }

  const data = {
    userId,
    savedAt: new Date(),
    lines: lines.map((line) => ({
      makat: line.makat,
      qty: line.qty,
      addedAt: line.addedAt,
      updatedAt: line.updatedAt,
    })),
  }

  await setDoc(doc(db, COLLECTION, userId), data)
}

/**
 * Load saved cart from Firestore
 * @param {string} userId - User UID
 * @returns {Promise<Object|null>} Saved cart data or null if not found
 */
export async function loadSavedCart(userId) {
  if (!userId) return null

  const db = getFirebaseDb()
  const snapshot = await getDoc(doc(db, COLLECTION, userId))

  return snapshot.exists() ? snapshot.data() : null
}

/**
 * Clear saved cart from Firestore
 * Called after successful checkout
 * @param {string} userId - User UID
 * @returns {Promise<void>}
 */
export async function clearSavedCart(userId) {
  if (!userId) return

  const db = getFirebaseDb()
  await deleteDoc(doc(db, COLLECTION, userId))
}
